import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, ArrowRightLeft, Trash2, Printer, FileSpreadsheet, Eye, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { exportToExcel } from '../utils/excelExport';

const fmtR = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const CashTransfers = () => {
  const { isAdmin, isAdminOnly, user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const [showSlip, setShowSlip] = useState(null);
  const [form, setForm] = useState({
    transfer_date: new Date().toISOString().split('T')[0],
    direction: 'bank_to_cash',
    amount: '',
    bank_name: '',
    bank_account_no: '',
    reference_no: '',
    description: '',
    slip_image: '',
    branch_id: user?.branch_id || '',
  });

  const fetchAll = async (silent = false) => {
    try {
      const [tRes, bRes] = await Promise.all([
        api.get('/cash-transfers', { params: { ...(branchFilter !== 'all' ? { branch_id: branchFilter } : {}), ...(directionFilter !== 'all' ? { direction: directionFilter } : {}) } }),
        api.get('/branches'),
      ]);
      setTransfers(tRes.data || []);
      setBranches(bRes.data || []);
    } catch { if (!silent) toast.error('Failed to load cash transfers'); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [branchFilter, directionFilter]);
  useAutoRefresh(() => fetchAll(true));

  const brName = (id) => branches.find(b => b.id === id)?.name || '-';

  const totals = useMemo(() => {
    const b2c = transfers.filter(t => t.direction === 'bank_to_cash').reduce((s, t) => s + Number(t.amount || 0), 0);
    const c2b = transfers.filter(t => t.direction === 'cash_to_bank').reduce((s, t) => s + Number(t.amount || 0), 0);
    return { b2c, c2b, net: b2c - c2b };
  }, [transfers]);

  const onSlipUpload = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Slip must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, slip_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleCreate = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter amount'); return; }
    if (!form.transfer_date) { toast.error('Pick a date'); return; }
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (!isAdmin()) delete payload.branch_id; // backend will use user's own branch
      await api.post('/cash-transfers', payload);
      toast.success('Cash transfer recorded');
      setShowNew(false);
      setForm({ transfer_date: new Date().toISOString().split('T')[0], direction: 'bank_to_cash', amount: '', bank_name: '', bank_account_no: '', reference_no: '', description: '', slip_image: '', branch_id: user?.branch_id || '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transfer?')) return;
    try { await api.delete(`/cash-transfers/${id}`); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed to delete'); }
  };

  const handlePrint = () => {
    const rows = transfers.map((t, i) => `<tr style="background:${i%2?'#f9fafb':'#fff'}"><td>${i+1}</td><td>${t.transfer_date||''}</td><td>${brName(t.branch_id)}</td><td>${t.direction === 'bank_to_cash' ? 'Bank → Cash' : 'Cash → Bank'}</td><td>${t.bank_name||'-'}</td><td>${t.reference_no||'-'}</td><td style="text-align:right">${fmtR(t.amount)}</td><td>${t.description||'-'}</td></tr>`).join('');
    const html = `<!doctype html><html><head><title>Cash Transfers — WWAGS MIS</title>
    <style>body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}h1{color:#0369a1;font-size:20px;margin:0 0 4px}.meta{color:#64748b;font-size:12px;margin-bottom:14px}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#0369a1;color:#fff;padding:8px;text-align:left}td{padding:6px 8px;border-bottom:1px solid #e2e8f0}.kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:12px}.kpi{border:1px solid #e2e8f0;border-radius:6px;padding:8px;background:#f8fafc}.kpi small{color:#64748b;font-size:10px;display:block}.kpi b{font-size:14px}@media print{body{-webkit-print-color-adjust:exact}@page{size:A4 landscape;margin:12mm}}</style>
    </head><body><h1>WWAGS MIS — Cash Transfer Register</h1>
    <p class="meta">Branch: <b>${branchFilter==='all'?'All':brName(branchFilter)}</b> · Direction: <b>${directionFilter}</b> · Total: <b>${transfers.length}</b> · Generated: ${new Date().toLocaleString('en-IN')}</p>
    <div class="kpis"><div class="kpi"><small>Bank → Cash</small><b style="color:#047857">${fmtR(totals.b2c)}</b></div><div class="kpi"><small>Cash → Bank</small><b style="color:#dc2626">${fmtR(totals.c2b)}</b></div><div class="kpi"><small>Net (B→C − C→B)</small><b>${fmtR(totals.net)}</b></div></div>
    <table><thead><tr><th>#</th><th>Date</th><th>Branch</th><th>Direction</th><th>Bank</th><th>Reference</th><th>Amount</th><th>Description</th></tr></thead><tbody>${rows||'<tr><td colspan="8" style="text-align:center;color:#94a3b8;padding:20px">No transfers</td></tr>'}</tbody></table>
    </body></html>`;
    const w = window.open('', '_blank'); if (w) { writePrintDoc(w.document, html); setTimeout(() => w.print(), 500); }
  };

  const handleExcel = () => {
    if (!transfers.length) { toast.error('Nothing to export'); return; }
    const rows = transfers.map((t, i) => ({
      'S.No.': i + 1,
      'Date': t.transfer_date,
      'Branch': brName(t.branch_id),
      'Direction': t.direction === 'bank_to_cash' ? 'Bank → Cash' : 'Cash → Bank',
      'Amount (Rs.)': Number(t.amount || 0),
      'Bank Name': t.bank_name || '',
      'Bank Account No.': t.bank_account_no || '',
      'Reference / Cheque No.': t.reference_no || '',
      'Description': t.description || '',
      'Slip Uploaded': t.slip_image ? 'Yes' : 'No',
      'Recorded By': t.created_by_email || '',
    }));
    exportToExcel({
      filename: 'WWAGS_Cash_Transfers',
      sheetName: 'Cash Transfers',
      rows,
      kpis: [
        { label: 'Bank → Cash Total', value: Math.round(totals.b2c) },
        { label: 'Cash → Bank Total', value: Math.round(totals.c2b) },
        { label: 'Net Movement', value: Math.round(totals.net) },
      ],
    });
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="cash-transfers-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ArrowRightLeft className="w-6 h-6 text-sky-700" />Cash Transfers (Bank ↔ Cash)</h1>
          <p className="text-sm text-slate-600">Record movements between Bank and Cash for each branch. Visible in the Cashbook automatically.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handlePrint} data-testid="ct-print"><Printer className="w-4 h-4 mr-1" />Print</Button>
          <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700" data-testid="ct-excel"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild>
              <Button className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="new-cash-transfer-btn"><Plus className="w-4 h-4 mr-1" />New Transfer</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Cash ↔ Bank Transfer</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Date</Label><Input type="date" value={form.transfer_date} onChange={e => setForm({ ...form, transfer_date: e.target.value })} data-testid="ct-date" /></div>
                  <div><Label className="text-xs">Direction</Label>
                    <Select value={form.direction} onValueChange={v => setForm({ ...form, direction: v })}>
                      <SelectTrigger data-testid="ct-direction"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_to_cash">Bank → Cash (cash withdrawal)</SelectItem>
                        <SelectItem value="cash_to_bank">Cash → Bank (cash deposit)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-xs">Amount (Rs.)</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="ct-amount" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Bank Name</Label><Input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} placeholder="SBI / HDFC / …" data-testid="ct-bank-name" /></div>
                  <div><Label className="text-xs">Account No.</Label><Input value={form.bank_account_no} onChange={e => setForm({ ...form, bank_account_no: e.target.value })} data-testid="ct-account-no" /></div>
                </div>
                <div><Label className="text-xs">Reference / Cheque / DD / UTR</Label><Input value={form.reference_no} onChange={e => setForm({ ...form, reference_no: e.target.value })} data-testid="ct-ref" /></div>
                {isAdmin() && (
                  <div><Label className="text-xs">Branch</Label>
                    <Select value={form.branch_id} onValueChange={v => setForm({ ...form, branch_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Pick branch" /></SelectTrigger>
                      <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Purpose of transfer" /></div>
                <div>
                  <Label className="text-xs">Deposit / Withdrawal Slip (optional)</Label>
                  <Input type="file" accept="image/*,application/pdf" onChange={e => onSlipUpload(e.target.files?.[0])} />
                  {form.slip_image && <p className="text-[10px] text-emerald-600 mt-1">Slip attached ✓</p>}
                </div>
                <Button onClick={handleCreate} className="w-full bg-sky-700 hover:bg-sky-800 text-white" data-testid="ct-create-confirm">Save Transfer</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4"><p className="text-xs text-emerald-700 font-medium flex items-center gap-1"><ArrowDownRight className="w-3 h-3" />Bank → Cash (Withdrawals)</p><p className="text-2xl font-bold text-emerald-900">{fmtR(totals.b2c)}</p></div>
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4"><p className="text-xs text-rose-700 font-medium flex items-center gap-1"><ArrowUpRight className="w-3 h-3" />Cash → Bank (Deposits)</p><p className="text-2xl font-bold text-rose-900">{fmtR(totals.c2b)}</p></div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4"><p className="text-xs text-slate-700 font-medium">Net Movement (B→C − C→B)</p><p className={`text-2xl font-bold ${totals.net >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>{fmtR(totals.net)}</p></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {isAdmin() && (
          <div>
            <Label className="text-xs">Branch</Label>
            <Select value={branchFilter} onValueChange={setBranchFilter}>
              <SelectTrigger data-testid="ct-branch-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs">Direction</Label>
          <Select value={directionFilter} onValueChange={setDirectionFilter}>
            <SelectTrigger data-testid="ct-direction-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Directions</SelectItem>
              <SelectItem value="bank_to_cash">Bank → Cash</SelectItem>
              <SelectItem value="cash_to_bank">Cash → Bank</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-sky-700 text-white"><tr>
              <th className="px-2 py-2 text-left">Date</th>
              <th className="px-2 py-2 text-left">Branch</th>
              <th className="px-2 py-2 text-center">Direction</th>
              <th className="px-2 py-2 text-right">Amount</th>
              <th className="px-2 py-2 text-left">Bank</th>
              <th className="px-2 py-2 text-left">Reference</th>
              <th className="px-2 py-2 text-left">Description</th>
              <th className="px-2 py-2 text-center">Slip</th>
              <th className="px-2 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {transfers.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400">No transfers yet — click "New Transfer".</td></tr>
              ) : transfers.map(t => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-2 py-1.5">{t.transfer_date}</td>
                  <td className="px-2 py-1.5">{brName(t.branch_id)}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border ${t.direction === 'bank_to_cash' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                      {t.direction === 'bank_to_cash' ? <><ArrowDownRight className="w-3 h-3" />Bank → Cash</> : <><ArrowUpRight className="w-3 h-3" />Cash → Bank</>}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right font-bold">{fmtR(t.amount)}</td>
                  <td className="px-2 py-1.5">{t.bank_name || '-'}{t.bank_account_no ? <span className="text-slate-400 text-[10px] block">A/c {t.bank_account_no}</span> : null}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{t.reference_no || '-'}</td>
                  <td className="px-2 py-1.5 text-[11px]">{t.description || '-'}</td>
                  <td className="px-2 py-1.5 text-center">{t.slip_image ? <button onClick={() => setShowSlip(t.slip_image)} className="text-blue-700 hover:underline" data-testid={`ct-view-slip-${t.id}`}><Eye className="w-3 h-3" /></button> : '—'}</td>
                  <td className="px-2 py-1.5 text-right">
                    {isAdminOnly() && <button onClick={() => handleDelete(t.id)} className="p-1 text-rose-700 hover:bg-rose-50 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!showSlip} onOpenChange={() => setShowSlip(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Transfer Slip</DialogTitle></DialogHeader>
          {showSlip && (showSlip.startsWith('data:application/pdf')
            ? <iframe src={showSlip} className="w-full h-[70vh]" title="Slip" />
            : <img src={showSlip} alt="Transfer slip" className="max-h-[70vh] mx-auto" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashTransfers;
